import apiService from './api';

export const authAPI = {
  // Login
  login: async (credentials) => {
    const response = await apiService.post('/auth/login', credentials);
    if (response.data.token) {
      apiService.setAuthToken(response.data.token);
    }
    return response;
  },

  // Verify client code (public endpoint for 2-step login)
  verifyClient: async (code) => {
    return await apiService.get(`/auth/verify-client?code=${encodeURIComponent(code)}`);
  },

  // Register (Admin only)
  register: async (userData) => {
    return await apiService.post('/auth/register', userData);
  },

  // Get current user
  getMe: async () => {
    return await apiService.get('/auth/me');
  },

  // Update profile
  updateProfile: async (profileData) => {
    return await apiService.put('/auth/profile', profileData);
  },

  // Change password
  changePassword: async (passwordData) => {
    return await apiService.put('/auth/change-password', passwordData);
  },

  // Logout
  logout: async () => {
    const response = await apiService.post('/auth/logout');
    apiService.removeAuthToken();
    return response;
  },

  // Check if user is authenticated
  isAuthenticated: () => {
    return !!apiService.getAuthToken();
  },

  // Forgot Password - Send OTP
  forgotPassword: async (email, clientCode) => {
    return await apiService.post('/auth/forgot-password', { email, clientCode });
  },

  // Verify OTP
  verifyOtp: async (email, otp, clientCode) => {
    return await apiService.post('/auth/verify-otp', { email, otp, clientCode });
  },

  // Reset Password
  resetPassword: async (email, otp, newPassword, clientCode) => {
    return await apiService.post('/auth/reset-password', { email, otp, newPassword, clientCode });
  }
};

export const customerAPI = {
  // Get customers with filters and pagination
  getCustomers: async (params = {}) => {
    try {
      const response = await apiService.get('/customers', params);
      return response;
    } catch (error) {
      console.error('customerAPI.getCustomers error:', error);
      throw error;
    }
  },

  // Get single customer
  getCustomer: async (id) => {
    return await apiService.get(`/customers/${id}`);
  },

  // Create customer
  createCustomer: async (customerData) => {
    return await apiService.post('/customers', customerData);
  },

  // Update customer
  updateCustomer: async (id, customerData) => {
    return await apiService.put(`/customers/${id}`, customerData);
  },

  // Delete customer
  deleteCustomer: async (id) => {
    return await apiService.delete(`/customers/${id}`);
  },

  // Get customer statistics
  getStats: async () => {
    try {
      const response = await apiService.get('/customers/stats');
      return response;
    } catch (error) {
      console.error('customerAPI.getStats error:', error);
      throw error;
    }
  },

  // Export to Excel
  exportToExcel: async () => {
    return await apiService.downloadFile('/customers/export/excel', 'customers.xlsx');
  },
  
  // Get upcoming birthdays
  getUpcomingBirthdays: () => apiService.get('/customers/birthdays'),
  
  // Send birthday wish
  sendBirthdayWish: (customerId) => apiService.post(`/customers/${customerId}/birthday-wish`)
};

export const userAPI = {
  // Get users (Admin only)
  getUsers: async (params = {}) => {
    try {
      const response = await apiService.get('/users', params);
      return response;
    } catch (error) {
      console.error('userAPI.getUsers error:', error);
      throw error;
    }
  },

  // Get single user
  getUser: async (id) => {
    return await apiService.get(`/users/${id}`);
  },

  // Create user (Admin only)
  createUser: async (userData) => {
    return await apiService.post('/users', userData);
  },

  // Update user
  updateUser: async (id, userData) => {
    return await apiService.put(`/users/${id}`, userData);
  },

  // Toggle user status (Admin only)
  toggleUserStatus: async (id, data = {}) => {
    return await apiService.patch(`/users/${id}/toggle-status`, data);
  },

  // Delete user (Admin only)
  deleteUser: async (id) => {
    return await apiService.delete(`/users/${id}`);
  },

  // Get user statistics (Admin only)
  getStats: async () => {
    try {
      const response = await apiService.get('/users/stats');
      return response;
    } catch (error) {
      console.error('userAPI.getStats error:', error);
      throw error;
    }
  },

  // Export employees to Excel (Admin only)
  exportToExcel: async () => {
    return await apiService.downloadFile('/users/export/excel', 'employees.xlsx');
  },

  // Bulk upload employees (Admin only)
  bulkUploadEmployees: async (employees) => {
    return await apiService.post('/users/bulk-upload', { employees });
  },

  // Download employee bulk upload template
  downloadBulkUploadTemplate: async () => {
    return await apiService.downloadFile('/users/bulk-upload/template', 'employee_upload_template.xlsx');
  },

  // Personal Details Self-Service
  getMyPersonalDetails: () => apiService.get('/users/me/personal-details'),
  submitPersonalDetails: (data) => apiService.put('/users/me/personal-details', data),
  getPendingPersonalDetails: () => apiService.get('/users/pending-personal'),
  approvePersonalDetails: (id) => apiService.put(`/users/${id}/personal-details/approve`),
  rejectPersonalDetails: (id, reason) => apiService.put(`/users/${id}/personal-details/reject`, { reason }),

  // Reporting Manager & Team
  getMyReportees: () => apiService.get('/users/my-reportees'),
  getReportingDropdown: () => apiService.get('/users/reporting-dropdown'),

  // Employee Type Actions (extend probation, confirm, terminate)
  employeeTypeAction: (id, data) => apiService.put(`/users/${id}/employee-type-action`, data),
};

export const locationAPI = {
  // Get locations
  getLocations: async (params = {}) => {
    try {
      const response = await apiService.get('/locations', params);
      return response; 
    } catch (error) {
      console.error('locationAPI.getLocations error:', error);
      throw error;
    }
  },

  // Get location names for dropdown
  getLocationNames: async () => {
    return await apiService.get('/locations/names');
  },

  // Get single location
  getLocation: async (id) => {
    return await apiService.get(`/locations/${id}`);
  },

  // Create location (Admin only)
  createLocation: async (locationData) => {
    return await apiService.post('/locations', locationData);
  },

  // Update location (Admin only)
  updateLocation: async (id, locationData) => {
    return await apiService.put(`/locations/${id}`, locationData);
  },

  // Toggle location status (Admin only)
  toggleLocationStatus: async (id) => {
    return await apiService.patch(`/locations/${id}/toggle-status`);
  },

  // Delete location (Admin only)
  deleteLocation: async (id) => {
    return await apiService.delete(`/locations/${id}`);
  },

  // Bulk upload locations (Admin only)
  bulkUploadLocations: async (locationsData) => {
    return await apiService.post('/locations/bulk-upload', locationsData);
  },

  // Get location statistics (Admin only)
  getStats: async () => {
    try {
      const response = await apiService.get('/locations/stats');
      return response;
    } catch (error) {
      console.error('locationAPI.getStats error:', error);
      throw error;
    }
  }
};

export const clientAPI = {
  // Public: active client logos for landing page (no auth required)
  getPublicLogos: () => apiService.get('/clients/public-logos'),

  // Get clients (SuperAdmin only)
  getClients: async (params = {}) => {
    try {
      const response = await apiService.get('/clients', params);
      return response;
    } catch (error) {
      console.error('clientAPI.getClients error:', error);
      throw error;
    }
  },

  // Get single client
  getClient: async (id) => {
    return await apiService.get(`/clients/${id}`);
  },

  // Create client (SuperAdmin only)
  createClient: async (clientData) => {
    // Check if clientData is FormData (for file uploads)
    if (clientData instanceof FormData) {
      return await apiService.postFormData('/clients', clientData);
    }
    return await apiService.post('/clients', clientData);
  },

  // Update client (SuperAdmin only)
  updateClient: async (id, clientData) => {
    // Check if clientData is FormData (for file uploads)
    if (clientData instanceof FormData) {
      return await apiService.putFormData(`/clients/${id}`, clientData);
    }
    return await apiService.put(`/clients/${id}`, clientData);
  },

  // Toggle client status (SuperAdmin only)
  toggleClientStatus: async (id) => {
    return await apiService.patch(`/clients/${id}/toggle-status`);
  },

  // Get delete preview (counts of related data) (SuperAdmin only)
  getDeletePreview: async (id) => {
    return await apiService.get(`/clients/${id}/delete-preview`);
  },

  // Delete client (SuperAdmin only)
  deleteClient: async (id) => {
    return await apiService.delete(`/clients/${id}`);
  },

  // Get client statistics (SuperAdmin only)
  getStats: async () => {
    try {
      const response = await apiService.get('/clients/stats');
      return response;
    } catch (error) {
      console.error('clientAPI.getStats error:', error);
      throw error;
    }
  },

  // Check subscription limits (SuperAdmin only)
  checkSubscriptionLimits: async (id) => {
    return await apiService.get(`/clients/${id}/limits`);
  },

  // Reset admin password (SuperAdmin only)
  resetAdminPassword: async (id, data) => {
    return await apiService.post(`/clients/${id}/reset-password`, data);
  },

  // Get client billing (SuperAdmin only)
  getClientBilling: async (id) => {
    return await apiService.get(`/clients/${id}/billing`);
  },

  // Get my client billing (Admin)
  getMyBilling: async () => {
    return await apiService.get('/clients/my/billing');
  },

  // Generate monthly billing (SuperAdmin only)
  generateBilling: async (id, month) => {
    return await apiService.post(`/clients/${id}/billing/generate`, { month });
  },

  // Mark payment as paid (SuperAdmin only)
  markPaymentPaid: async (id, data) => {
    return await apiService.post(`/clients/${id}/billing/pay`, data);
  },

  // Update client subscription (SuperAdmin only)
  updateSubscription: async (id, data) => {
    return await apiService.put(`/clients/${id}/subscription`, data);
  }
};

// Analytics API
export const analyticsAPI = {
  // Get system analytics (SuperAdmin only)
  getSystemAnalytics: async () => {
    try {
      const response = await apiService.get('/analytics/system');
      return response;
    } catch (error) {
      console.error('analyticsAPI.getSystemAnalytics error:', error);
      throw error;
    }
  },

  // Get client analytics
  getClientAnalytics: async () => {
    try {
      const response = await apiService.get('/analytics/client');
      return response;
    } catch (error) {
      console.error('analyticsAPI.getClientAnalytics error:', error);
      throw error;
    }
  }
};

// Attendance API
export const attendanceAPI = {
  // Employee actions - Punch in/out with photo (FormData binary)
  punchIn: async ({ location, photo }) => {
    const formData = new FormData();
    formData.append('latitude', location.latitude.toString());
    formData.append('longitude', location.longitude.toString());
    if (location.accuracy) {
      formData.append('accuracy', location.accuracy.toString());
    }
    // Send location error if present
    if (location.error) {
      formData.append('error', location.error);
    }
    
    // Convert base64 to blob if photo provided
    if (photo) {
      const response = await fetch(photo);
      const blob = await response.blob();
      formData.append('photo', blob, 'punch-in-photo.jpg');
    }
    
    try {
      const result = await apiService.postFormData('/attendance/punch-in', formData);
      return result;
    } catch (error) {
      console.error('attendanceAPI.punchIn error:', error);
      throw error;
    }
  },

  punchOut: async ({ location, photo }) => {
    const formData = new FormData();
    formData.append('latitude', location.latitude.toString());
    formData.append('longitude', location.longitude.toString());
    if (location.accuracy) {
      formData.append('accuracy', location.accuracy.toString());
    }
    // Send location error if present
    if (location.error) {
      formData.append('error', location.error);
    }
    
    // Convert base64 to blob if photo provided
    if (photo) {
      const response = await fetch(photo);
      const blob = await response.blob();
      formData.append('photo', blob, 'punch-out-photo.jpg');
    }
    
    try {
      const result = await apiService.postFormData('/attendance/punch-out', formData);
      return result;
    } catch (error) {
      console.error('attendanceAPI.punchOut error:', error);
      throw error;
    }
  },

  // Periodic location check
  addLocationCheck: async (locationData) => {
    try {
      const response = await apiService.post('/attendance/location-check', locationData);
      return response;
    } catch (error) {
      console.error('❌ attendanceAPI.addLocationCheck error:', error);
      throw error;
    }
  },

  // Get today's attendance
  getTodayAttendance: async () => {
    try {
      const response = await apiService.get('/attendance/today');
      return response;
    } catch (error) {
      console.error('❌ attendanceAPI.getTodayAttendance error:', error);
      throw error;
    }
  },

  // Get my attendance history
  getMyHistory: async (params = {}) => {
    try {
      const response = await apiService.get('/attendance/my-history', params);
      return response;
    } catch (error) {
      console.error('attendanceAPI.getMyHistory error:', error);
      throw error;
    }
  },

  // Admin: Get employees attendance
  getEmployeesAttendance: async (params = {}) => {
    try {
      const response = await apiService.get('/attendance/employees', params);
      return response;
    } catch (error) {
      console.error('attendanceAPI.getEmployeesAttendance error:', error);
      throw error;
    }
  },

  // Admin: Get specific employee's attendance history
  getEmployeeAttendance: async (employeeId, params = {}) => {
    try {
      const response = await apiService.get(`/attendance/employee/${employeeId}`, params);
      return response;
    } catch (error) {
      console.error('attendanceAPI.getEmployeeAttendance error:', error);
      throw error;
    }
  },

  // Admin: Get specific employee's attendance by date
  getAttendanceByDate: async (employeeId, date) => {
    try {
      const response = await apiService.get(`/attendance/employee/${employeeId}/date/${date}`);
      return response;
    } catch (error) {
      console.error('attendanceAPI.getAttendanceByDate error:', error);
      throw error;
    }
  },

  // Admin: Get employee stats
  getEmployeeStats: async (employeeId, params = {}) => {
    try {
      const response = await apiService.get(`/attendance/stats/${employeeId}`, params);
      return response;
    } catch (error) {
      console.error('attendanceAPI.getEmployeeStats error:', error);
      throw error;
    }
  },

  // Admin: Get dashboard stats
  getDashboardStats: async () => {
    try {
      const response = await apiService.get('/attendance/dashboard-stats');
      return response;
    } catch (error) {
      console.error('attendanceAPI.getDashboardStats error:', error);
      throw error;
    }
  },

  // Admin: Get today's present employees report
  getTodayPresentReport: async (params = {}) => {
    try {
      const response = await apiService.get('/attendance/today-present-report', params);
      return response;
    } catch (error) {
      console.error('❌ attendanceAPI.getTodayPresentReport error:', error);
      throw error;
    }
  },

  // Admin: Export today's attendance report to Excel
  exportTodayAttendanceReport: async (date, location) => {
    const params = {};
    if (date) params.date = date;
    if (location) params.location = location;
    
    const queryString = new URLSearchParams(params).toString();
    const filename = `Attendance_Report_${date || 'Today'}_${location || 'All'}.xlsx`;
    return await apiService.downloadFile(`/attendance/export-today-report?${queryString}`, filename);
  },

  // ==================== TEAM ====================

  // Get team attendance (for reporting managers)
  getTeamAttendance: (params = {}) => apiService.get('/attendance/team', params),

  // Get specific reportee's attendance history (for reporting managers)
  getReporteeAttendanceHistory: (employeeId, params = {}) => apiService.get(`/attendance/team/${employeeId}/history`, params),

  // ==================== REGULARIZATION ====================
  
  // Get my work timings (shift or organization timings)
  getMyWorkTimings: async () => {
    try {
      const response = await apiService.get('/attendance/my-work-timings');
      return response;
    } catch (error) {
      console.error('attendanceAPI.getMyWorkTimings error:', error);
      throw error;
    }
  },

  // Apply for regularization
  applyRegularization: async (data) => {
    try {
      const response = await apiService.post('/attendance/regularization', data);
      return response;
    } catch (error) {
      console.error('attendanceAPI.applyRegularization error:', error);
      throw error;
    }
  },

  // Get my regularization requests
  getMyRegularizations: async (params = {}) => {
    try {
      const response = await apiService.get('/attendance/regularization/my', params);
      return response;
    } catch (error) {
      console.error('attendanceAPI.getMyRegularizations error:', error);
      throw error;
    }
  },

  // Admin: Get pending regularization requests
  getPendingRegularizations: async () => {
    try {
      const response = await apiService.get('/attendance/regularization/pending');
      return response;
    } catch (error) {
      console.error('attendanceAPI.getPendingRegularizations error:', error);
      throw error;
    }
  },

  // Admin: Get all regularization requests
  getAllRegularizations: async (params = {}) => {
    try {
      const response = await apiService.get('/attendance/regularization', params);
      return response;
    } catch (error) {
      console.error('attendanceAPI.getAllRegularizations error:', error);
      throw error;
    }
  },

  // Admin: Approve regularization
  approveRegularization: async (id, data = {}) => {
    try {
      const response = await apiService.put(`/attendance/regularization/${id}/approve`, data);
      return response;
    } catch (error) {
      console.error('attendanceAPI.approveRegularization error:', error);
      throw error;
    }
  },

  // Admin: Reject regularization (marks as LOP)
  rejectRegularization: async (id, data = {}) => {
    try {
      const response = await apiService.put(`/attendance/regularization/${id}/reject`, data);
      return response;
    } catch (error) {
      console.error('attendanceAPI.rejectRegularization error:', error);
      throw error;
    }
  },

  // Bulk approve regularization requests
  bulkApproveRegularizations: async (regularizationIds, data = {}) => {
    try {
      const response = await apiService.post('/attendance/regularization/bulk-approve', { 
        regularizationIds, 
        ...data 
      });
      return response;
    } catch (error) {
      console.error('attendanceAPI.bulkApproveRegularizations error:', error);
      throw error;
    }
  },

  // Bulk reject regularization requests
  bulkRejectRegularizations: async (regularizationIds, data = {}) => {
    try {
      const response = await apiService.post('/attendance/regularization/bulk-reject', {
        regularizationIds,
        ...data
      });
      return response;
    } catch (error) {
      console.error('attendanceAPI.bulkRejectRegularizations error:', error);
      throw error;
    }
  },

  // Request to revoke attendance
  requestRevoke: async (data) => {
    try {
      return await apiService.post('/attendance/revoke', data);
    } catch (error) {
      console.error('attendanceAPI.requestRevoke error:', error);
      throw error;
    }
  },

  // Get own revoke requests
  getMyRevokes: async (params = {}) => {
    try {
      return await apiService.get('/attendance/revoke/my', params);
    } catch (error) {
      console.error('attendanceAPI.getMyRevokes error:', error);
      throw error;
    }
  },

  // Manager/Admin: get pending revoke requests
  getPendingRevokes: async () => {
    try {
      return await apiService.get('/attendance/revoke/pending');
    } catch (error) {
      console.error('attendanceAPI.getPendingRevokes error:', error);
      throw error;
    }
  },

  // Manager/Admin: approve revoke
  approveRevoke: async (id, data = {}) => {
    try {
      return await apiService.put(`/attendance/revoke/${id}/approve`, data);
    } catch (error) {
      console.error('attendanceAPI.approveRevoke error:', error);
      throw error;
    }
  },

  // Manager/Admin: reject revoke
  rejectRevoke: async (id, data = {}) => {
    try {
      return await apiService.put(`/attendance/revoke/${id}/reject`, data);
    } catch (error) {
      console.error('attendanceAPI.rejectRevoke error:', error);
      throw error;
    }
  }
};

// ==================== LEAVE MANAGEMENT API ====================

export const leaveAPI = {
  // Apply for leave
  applyLeave: (data) => apiService.post('/leaves/apply', data),
  
  // Get leave balance for current user or specific employee
  getLeaveBalance: (params = {}) => apiService.get('/leaves/balance', params),

  // Month-by-month credit history behind the balance
  getLeaveCreditHistory: (params = {}) => apiService.get('/leaves/credit-history', params),

  // Get leave requests (own or all for admin)
  getLeaveRequests: (params = {}) => apiService.get('/leaves', params),
  
  // Get pending leave requests (admin/manager only)
  getPendingLeaves: () => apiService.get('/leaves/pending'),
  
  // Get team leave history (all statuses - admin/manager/reporter)
  getTeamLeaveHistory: (params = {}) => apiService.get('/leaves/team-history', params),
  
  // Get employee leaves (admin tracking specific employee)
  getEmployeeLeaves: (employeeId, params = {}) => apiService.get(`/leaves/employee/${employeeId}`, params),
  
  // Approve leave request
  approveLeave: (id, comments = '') => apiService.put(`/leaves/${id}/approve`, { comments }),
  
  // Reject leave request
  rejectLeave: (id, comments) => apiService.put(`/leaves/${id}/reject`, { comments }),
  
  // Bulk approve leave requests
  bulkApproveLeaves: (leaveIds, comments = '') => apiService.post('/leaves/bulk-approve', { leaveIds, comments }),
  
  // Bulk reject leave requests
  bulkRejectLeaves: (leaveIds, comments) => apiService.post('/leaves/bulk-reject', { leaveIds, comments }),
  
  // Cancel own leave request
  cancelLeave: (id) => apiService.put(`/leaves/${id}/cancel`),
  
  // Admin cancel approved leave request
  adminCancelLeave: (id, comments = '') => apiService.put(`/leaves/${id}/admin-cancel`, { comments }),

  // Admin update leave request (dates, type, reason)
  adminUpdateLeave: (id, data) => apiService.put(`/leaves/${id}/admin-update`, data),
  
  // Get leave stats (admin/manager only)
  getLeaveStats: (params = {}) => apiService.get('/leaves/stats', params),
  
  // Update leave balance (admin only)
  updateLeaveBalance: (employeeId, data) => apiService.put(`/leaves/balance/${employeeId}`, data),
  
  // Get all employees' leave balances (admin only)
  getAllLeaveBalances: (params = {}) => apiService.get('/leaves/all-balances', params),
  
  // Sync all leave balances to match client settings (admin only)
  syncLeaveBalances: (data = {}) => apiService.post('/leaves/sync-balances', data),
  
  // Get monthly leave tracking (employee's own or specific employee for admin)
  getMonthlyTracking: (params = {}) => apiService.get('/leaves/monthly-tracking', params),
};

// ==================== PAYROLL API ====================

export const payrollAPI = {
  // Salary Configuration
  createSalaryConfig: (data) => apiService.post('/payroll/salary-config', data),
  getSalaryConfig: (employeeId) => apiService.get(`/payroll/salary-config/${employeeId}`),
  getAllSalaryConfigs: () => apiService.get('/payroll/salary-configs'),
  
  // Payslip Generation
  generatePayslip: (data) => apiService.post('/payroll/generate-payslip', data),
  generateBulkPayslips: (data) => apiService.post('/payroll/generate-bulk', data),
  generateBulkAndEmail: (data) => apiService.post('/payroll/generate-bulk-email', data),
  
  // Payslip Retrieval
  getPayslip: (id) => apiService.get(`/payroll/payslip/${id}`),
  getPayslips: (params = {}) => apiService.get('/payroll/payslips', params),
  getAllPayslips: (params = {}) => apiService.get('/payroll/all-payslips', params),
  
  // Payslip Upload (for admin to upload PDFs)
  uploadPayslip: (formData) => apiService.postFormData('/payroll/upload-payslip', formData),
  uploadPayslipDocument: (id, formData) => apiService.postFormData(`/payroll/payslip/${id}/upload`, formData),
  
  // Payslip Approval & Payment
  approvePayslip: (id) => apiService.put(`/payroll/payslip/${id}/approve`),
  markAsPaid: (id, data) => apiService.put(`/payroll/payslip/${id}/pay`, data),
  deletePayslip: (id) => apiService.delete(`/payroll/payslip/${id}`),
  
  // Get employees for payroll dropdown
  getEmployeesForPayroll: () => apiService.get('/payroll/employees'),

  // Get PF-eligible employees with wage/attendance data for ECR generation
  getPfEcrEmployees: (month, year) => apiService.get(`/payroll/pf/eligible-employees?month=${month}&year=${year}`),
  
  // Payroll Stats
  getPayrollStats: (params = {}) => apiService.get('/payroll/stats', params),
  
  // Salary Advance/Loan
  requestAdvance: (data) => apiService.post('/payroll/advance/request', data),
  processAdvanceRequest: (id, action) => apiService.put(`/payroll/advance/${id}/approve`, { action }),
  disburseAdvance: (id) => apiService.put(`/payroll/advance/${id}/disburse`),
  
  // Export Payroll to Excel
  exportToExcel: async (month, year) => {
    return await apiService.downloadFile(`/payroll/export/excel?month=${month}&year=${year}`, `Payroll_${month}_${year}.xlsx`);
  },

  // Export Single Employee Payroll to Excel
  exportSingleEmployeeToExcel: async (month, year, employeeId, employeeName) => {
    const filename = `Payroll_${employeeName || 'Employee'}_${month}_${year}.xlsx`.replace(/[^a-zA-Z0-9._-]/g, '_');
    return await apiService.downloadFile(`/payroll/export/employee-excel?month=${month}&year=${year}&employeeId=${employeeId}`, filename);
  },

  // Export Attendance Only to Excel
  exportAttendanceToExcel: async (month, year) => {
    return await apiService.downloadFile(`/payroll/export/attendance?month=${month}&year=${year}`, `Attendance_${month}_${year}.xlsx`);
  },

  // Email Payslip PDF
  emailPayslip: (id) => apiService.post(`/payroll/payslip/${id}/email`),
};

// ==================== LEAVE ENCASHMENT API ====================

export const leaveEncashmentAPI = {
  getConfig: () => apiService.get('/encashment/config'),
  updateLeaveTypesConfig: (data) => apiService.put('/encashment/config/leave-types', data),
  updateFormulaConfig: (data) => apiService.put('/encashment/config/formula', data),
  updateCarryForwardConfig: (data) => apiService.put('/encashment/config/carry-forward', data),
  updateTriggersConfig: (data) => apiService.put('/encashment/config/triggers', data),
  updateApprovalConfig: (data) => apiService.put('/encashment/config/approval', data),

  getBalance: (employeeId, params = {}) => apiService.get(`/encashment/balance/${employeeId}`, params),
  previewRequest: (data) => apiService.post('/encashment/requests/preview', data),
  createRequest: (data) => apiService.post('/encashment/requests', data),
  getRequests: (params = {}) => apiService.get('/encashment/requests', params),
  approveRequest: (id, comments = '') => apiService.post(`/encashment/requests/${id}/approve`, { comments }),
  rejectRequest: (id, comments) => apiService.post(`/encashment/requests/${id}/reject`, { comments }),
};

// ==================== BONUS API ====================

export const bonusAPI = {
  getCycle: (year, cycle) => apiService.get(`/bonus/${year}/${cycle}`),
  updateRule: (year, cycle, rule) => apiService.put(`/bonus/${year}/${cycle}/rule`, { rule }),
  disqualify: (year, cycle, data) => apiService.post(`/bonus/${year}/${cycle}/disqualifications`, data),
  reinstate: (year, cycle, employeeId) => apiService.delete(`/bonus/${year}/${cycle}/disqualifications/${employeeId}`),
  finalize: (year, cycle) => apiService.post(`/bonus/${year}/${cycle}/finalize`),
  reopen: (year, cycle) => apiService.post(`/bonus/${year}/${cycle}/reopen`),
};

// ==================== CLIENT SETTINGS API ====================

export const clientSettingsAPI = {
  // Get my client settings (for admin)
  getMySettings: () => apiService.get('/clients/my-settings'),
  
  // Update my client settings (for admin)
  updateMySettings: (data) => apiService.put('/clients/my-settings', data),
  
  // Employee ID Auto-Generation
  getNextEmployeeId: () => apiService.get('/clients/my-settings/next-employee-id'),
  updateEmployeeIdSettings: (data) => apiService.put('/clients/my-settings/employee-id-settings', data),
  incrementEmployeeIdNumber: () => apiService.post('/clients/my-settings/increment-employee-id'),
  
  // Office Location Management
  addOfficeLocation: (data) => apiService.post('/clients/my-settings/office-locations', data),
  updateOfficeLocation: (locationId, data) => apiService.put(`/clients/my-settings/office-locations/${locationId}`, data),
  deleteOfficeLocation: (locationId) => apiService.delete(`/clients/my-settings/office-locations/${locationId}`),
  
  // Department Management
  getDepartments: () => apiService.get('/clients/my-settings/departments'),
  addDepartment: (data) => apiService.post('/clients/my-settings/departments', typeof data === 'string' ? { name: data } : data),
  updateDepartment: (id, data) => apiService.put(`/clients/my-settings/departments/${id}`, typeof data === 'string' ? { name: data } : data),
  deleteDepartment: (id) => apiService.delete(`/clients/my-settings/departments/${id}`),

  // Designation Management
  getDesignations: () => apiService.get('/clients/my-settings/designations'),
  addDesignation: (name, department, rank) => apiService.post('/clients/my-settings/designations', { name, department, rank }),
  updateDesignation: (id, name, department, rank) => apiService.put(`/clients/my-settings/designations/${id}`, { name, department, rank }),
  deleteDesignation: (id) => apiService.delete(`/clients/my-settings/designations/${id}`),

  // Logo Management
  uploadLogo: async (file) => {
    const formData = new FormData();
    formData.append('logo', file);
    return apiService.postFormData('/clients/my-settings/logo', formData);
  },
  deleteLogo: () => apiService.delete('/clients/my-settings/logo'),

  // Payroll Signature Management
  uploadSignature: async (file) => {
    const formData = new FormData();
    formData.append('signature', file);
    return apiService.postFormData('/clients/my-settings/signature', formData);
  },
  deleteSignature: () => apiService.delete('/clients/my-settings/signature'),
};

// ==================== BRANCH API ====================
// Org-chart branches used by the Multi-Level Approval Chain Engine for
// branch-wise departments (see backend/src/models/Branch.js)
export const branchAPI = {
  getBranches: () => apiService.get('/branches'),
  addBranch: (data) => apiService.post('/branches', data),
  updateBranch: (id, data) => apiService.put(`/branches/${id}`, data),
  deleteBranch: (id) => apiService.delete(`/branches/${id}`),
};

// ==================== HOLIDAY API ====================

export const shiftAPI = {
  // Get all shifts
  getShifts: () => apiService.get('/shifts'),
  
  // Get single shift
  getShift: (id) => apiService.get(`/shifts/${id}`),
  
  // Create shift
  createShift: (data) => apiService.post('/shifts', data),
  
  // Update shift
  updateShift: (id, data) => apiService.put(`/shifts/${id}`, data),
  
  // Delete shift
  deleteShift: (id) => apiService.delete(`/shifts/${id}`),
  
  // Bulk assign shift to employees
  assignShift: (shiftId, employeeIds) => apiService.put(`/shifts/${shiftId}/assign`, { employeeIds }),
};

// ==================== HOLIDAY API ====================

export const holidayAPI = {
  // Get holidays for current client
  getHolidays: (params = {}) => apiService.get('/holidays', params),
  
  // Get single holiday
  getHoliday: (id) => apiService.get(`/holidays/${id}`),
  
  // Create holiday (admin only)
  createHoliday: (data) => apiService.post('/holidays', data),
  
  // Update holiday (admin only)
  updateHoliday: (id, data) => apiService.put(`/holidays/${id}`, data),
  
  // Delete holiday (admin only)
  deleteHoliday: (id) => apiService.delete(`/holidays/${id}`),
  
  // Bulk create holidays (admin only)
  bulkCreateHolidays: (holidays) => apiService.post('/holidays/bulk', { holidays }),
};

// ==================== LETTER API ====================

export const letterAPI = {
  // Admin - Generate letters for employees
  getOfferLetter: (employeeId) => apiService.downloadFile(`/letters/offer/${employeeId}`, 'offer_letter.pdf'),
  getAppointmentLetter: (employeeId) => apiService.downloadFile(`/letters/appointment/${employeeId}`, 'appointment_letter.pdf'),
  getConfirmationLetter: (employeeId) => apiService.downloadFile(`/letters/confirmation/${employeeId}`, 'confirmation_letter.pdf'),
  getExperienceLetter: (employeeId, lastWorkingDate) => {
    const url = lastWorkingDate 
      ? `/letters/experience/${employeeId}?lastWorkingDate=${lastWorkingDate}` 
      : `/letters/experience/${employeeId}`;
    return apiService.downloadFile(url, 'experience_letter.pdf');
  },
  
  // Employee - Download own letters
  getMyOfferLetter: () => apiService.downloadFile('/letters/my/offer', 'my_offer_letter.pdf'),
  getMyAppointmentLetter: () => apiService.downloadFile('/letters/my/appointment', 'my_appointment_letter.pdf'),
  getMyExperienceLetter: () => apiService.downloadFile('/letters/my/experience', 'my_experience_letter.pdf'),

  // Admin - Upload custom letters
  uploadLetter: (employeeId, formData) => apiService.postFormData(`/letters/upload/${employeeId}`, formData),
  getAllUploadedLetters: () => apiService.get('/letters/all-uploaded'),
  getUploadedLetters: (employeeId, letterType) => {
    const params = letterType ? `?letterType=${letterType}` : '';
    return apiService.get(`/letters/uploaded/${employeeId}${params}`);
  },
  deleteUploadedLetter: (letterId) => apiService.delete(`/letters/uploaded/${letterId}`),
  downloadUploadedLetter: (letterId) => apiService.downloadFile(`/letters/download/${letterId}`, 'letter.pdf'),

  // Employee - Get own uploaded letters
  getMyUploadedLetters: (letterType) => {
    const params = letterType ? `?letterType=${letterType}` : '';
    return apiService.get(`/letters/my/uploaded${params}`);
  },
};

// ==================== EXPENDITURE API (Super Admin) ====================

export const expenditureAPI = {
  // Get all expenditures with filters
  getExpenditures: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiService.get(`/expenditures${queryString ? `?${queryString}` : ''}`);
  },
  
  // Get single expenditure
  getExpenditure: (id) => apiService.get(`/expenditures/${id}`),
  
  // Create new expenditure (with optional receipt file)
  createExpenditure: (formData) => apiService.postFormData('/expenditures', formData),
  
  // Update expenditure
  updateExpenditure: (id, formData) => apiService.putFormData(`/expenditures/${id}`, formData),
  
  // Delete expenditure
  deleteExpenditure: (id) => apiService.delete(`/expenditures/${id}`),
  
  // Get statistics
  getStats: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiService.get(`/expenditures/stats${queryString ? `?${queryString}` : ''}`);
  },
  
  // Delete receipt only
  deleteReceipt: (id) => apiService.delete(`/expenditures/${id}/receipt`),
};

// ==================== PROFIT API (Super Admin) ====================

export const profitAPI = {
  // Get all profits with filters
  getProfits: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiService.get(`/profits${queryString ? `?${queryString}` : ''}`);
  },
  
  // Get single profit
  getProfit: (id) => apiService.get(`/profits/${id}`),
  
  // Create new profit (with optional receipt file)
  createProfit: (formData) => apiService.postFormData('/profits', formData),
  
  // Update profit
  updateProfit: (id, formData) => apiService.putFormData(`/profits/${id}`, formData),
  
  // Delete profit
  deleteProfit: (id) => apiService.delete(`/profits/${id}`),
  
  // Get statistics
  getStats: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiService.get(`/profits/stats${queryString ? `?${queryString}` : ''}`);
  },
  
  // Delete receipt only
  deleteReceipt: (id) => apiService.delete(`/profits/${id}/receipt`),
};

// ==================== RESIGNATION API ====================

export const resignationAPI = {
  // Employee - Submit resignation
  submitResignation: (data) => apiService.post('/resignations', data),
  
  // Employee - Get my resignation
  getMyResignation: () => apiService.get('/resignations/my-resignation'),
  
  // Employee - Withdraw resignation
  withdrawResignation: (id, data) => apiService.put(`/resignations/${id}/withdraw`, data),
  
  // Reporting Manager - Get team resignations
  getTeamResignations: () => apiService.get('/resignations/my-team'),
  
  // Reporting Manager - Action on resignation
  reporterAction: (id, data) => apiService.put(`/resignations/${id}/reporter-action`, data),
  
  // Admin - Get all resignations
  getAllResignations: (params = {}) => apiService.get('/resignations', params),
  
  // Admin - Get resignation details
  getResignationDetails: (id) => apiService.get(`/resignations/${id}`),
  
  // Admin - Action on resignation
  adminAction: (id, data) => apiService.put(`/resignations/${id}/admin-action`, data),
  
  // Admin - Update exit checklist
  updateExitChecklist: (id, data) => apiService.put(`/resignations/${id}/exit-checklist`, data),
  
  // Admin - Complete resignation
  completeResignation: (id, data) => apiService.put(`/resignations/${id}/complete`, data),
};

// ==================== TALENT ACQUISITION - JOBS API ====================

export const jobAPI = {
  // Dashboard
  getDashboard: () => apiService.get('/jobs/dashboard'),
  
  // Get TA Team for assignment dropdown
  getTATeam: () => apiService.get('/jobs/ta-team'),
  
  // Get all jobs with filters
  getJobs: (params = {}) => apiService.get('/jobs', params),
  
  // Get single job
  getJob: (id) => apiService.get(`/jobs/${id}`),
  
  // HOD - Create job request
  createJob: (data) => apiService.post('/jobs', data),
  
  // Update job
  updateJob: (id, data) => apiService.put(`/jobs/${id}`, data),
  
  // HR Head - Approve/Reject job
  approveJob: (id, data) => apiService.put(`/jobs/${id}/approve`, data),
  
  // Assign job to TA Executive
  assignJob: (id, data) => apiService.put(`/jobs/${id}/assign`, data),
  
  // TAT Extension
  requestTATExtension: (id, data) => apiService.post(`/jobs/${id}/extend-tat`, data),
  approveTATExtension: (id, extensionId, data) => apiService.put(`/jobs/${id}/extend-tat/${extensionId}`, data),
  
  // Close job
  closeJob: (id, data) => apiService.put(`/jobs/${id}/close`, data),
};

// ==================== TALENT ACQUISITION - CANDIDATES API ====================

export const candidateAPI = {
  // Get pipeline view
  getPipeline: (jobId) => apiService.get(`/candidates/pipeline/${jobId}`),
  
  // Get all candidates
  getCandidates: (params = {}) => apiService.get('/candidates', params),
  
  // Get single candidate
  getCandidate: (id) => apiService.get(`/candidates/${id}`),
  
  // Add candidate
  addCandidate: (data) => apiService.post('/candidates', data),
  
  // Update candidate
  updateCandidate: (id, data) => apiService.put(`/candidates/${id}`, data),
  
  // Move to stage (manual/admin override)
  updateStage: (id, data) => apiService.put(`/candidates/${id}/stage`, data),

  // TA: shortlist a sourced candidate
  shortlist: (id, data) => apiService.post(`/candidates/${id}/shortlist`, data),

  // TA: list eligible Manager/HOD interviewers to assign
  getInterviewers: (roundType) => apiService.get('/candidates/interviewers', { roundType }),

  // Screening
  updateScreening: (id, data) => apiService.put(`/candidates/${id}/screening`, data),

  // Interview management (scheduling is TA-only; feedback is the assigned Manager/HOD only)
  scheduleInterview: (id, data) => apiService.post(`/candidates/${id}/interview`, data),
  submitInterviewFeedback: (id, interviewId, data) => apiService.put(`/candidates/${id}/interview/${interviewId}/feedback`, data),
  submitLatestInterviewFeedback: (id, data) => apiService.put(`/candidates/${id}/interview/latest/feedback`, data),

  // TA: move an HOD-selected candidate into the CTC round
  moveToCTC: (id, data) => apiService.put(`/candidates/${id}/move-to-ctc`, data),

  // CTC Discussion
  updateCTCDiscussion: (id, data) => apiService.put(`/candidates/${id}/ctc`, data),

  // Offer management
  releaseOffer: (id, data) => apiService.put(`/candidates/${id}/offer`, data),
  updateOfferStatus: (id, data) => apiService.put(`/candidates/${id}/offer-status`, data),
  
  // Joining
  markAsJoined: (id, data) => apiService.put(`/candidates/${id}/joined`, data),
  
  // Notes
  addNote: (id, data) => apiService.post(`/candidates/${id}/notes`, data),
  
  // Upload resume (FormData)
  uploadResume: (id, formData) => apiService.postFormData(`/candidates/${id}/resume`, formData),
};

// ==================== CONTACT FORM API ====================

export const contactAPI = {
  // Submit contact form (public - no auth required)
  submitContactForm: (data) => apiService.post('/contact/submit', data),
  
  // Get all submissions (superadmin only)
  getSubmissions: (params = {}) => apiService.get('/contact/submissions', params),
  
  // Get statistics (superadmin only)
  getStats: () => apiService.get('/contact/stats'),
  
  // Get single submission (superadmin only)
  getSubmission: (id) => apiService.get(`/contact/submissions/${id}`),
  
  // Update submission status/notes (superadmin only)
  updateSubmission: (id, data) => apiService.patch(`/contact/submissions/${id}`, data),
  
  // Delete submission (superadmin only)
  deleteSubmission: (id) => apiService.delete(`/contact/submissions/${id}`)
};

// ==================== ADVANCE/LOAN API ====================

export const advanceAPI = {
  // Employee - Request advance/loan
  requestAdvance: (data) => apiService.post('/advances/request', data),
  
  // Employee - Get my advance requests
  getMyAdvanceRequests: (status) => {
    const params = status ? `?status=${status}` : '';
    return apiService.get(`/advances/my-requests${params}`);
  },
  
  // Employee - Cancel pending request
  cancelAdvanceRequest: (id) => apiService.put(`/advances/${id}/cancel`),
  
  // Authorized User - Get pending requests
  getPendingAdvanceRequests: () => apiService.get('/advances/pending'),
  
  // Authorized User - Get all requests with filters
  getAllAdvanceRequests: (params = {}) => apiService.get('/advances', params),
  
  // Authorized User - Get statistics
  getAdvanceStatistics: () => apiService.get('/advances/statistics'),
  
  // Authorized User - Approve request
  approveAdvanceRequest: (id, data) => apiService.put(`/advances/${id}/approve`, data),
  
  // Authorized User - Reject request
  rejectAdvanceRequest: (id, data) => apiService.put(`/advances/${id}/reject`, data),
  
  // Authorized User - Mark as disbursed
  disburseAdvance: (id, data) => apiService.put(`/advances/${id}/disburse`, data),
};

// Expenses API
export const expenseAPI = {
  // Employee - submit new expense (FormData for optional receipt upload)
  createExpense: (data) => {
    const formData = new FormData();
    formData.append('title', data.title);
    formData.append('amount', data.amount);
    formData.append('category', data.category);
    formData.append('expenseDate', data.expenseDate);
    if (data.description) formData.append('description', data.description);
    if (data.receipt) formData.append('receipt', data.receipt);
    return apiService.postFormData('/expenses', formData);
  },

  // Employee - get my expenses
  getMyExpenses: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiService.get(`/expenses/my${query ? '?' + query : ''}`);
  },

  // Employee - delete a pending expense
  deleteExpense: (id) => apiService.delete(`/expenses/${id}`),

  // Admin - get all expenses
  getAllExpenses: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiService.get(`/expenses${query ? '?' + query : ''}`);
  },

  // Admin - get stats
  getExpenseStats: () => apiService.get('/expenses/stats'),

  // Admin - approve
  approveExpense: (id, data = {}) => apiService.put(`/expenses/${id}/approve`, data),

  // Admin - reject
  rejectExpense: (id, data = {}) => apiService.put(`/expenses/${id}/reject`, data),
};

// ==================== SUPPORT CENTRE API ====================

const attachmentsFormData = (files) => {
  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));
  return formData;
};

const buildSupportGroup = (systemPath) => ({
  create: (data) => apiService.post(`/support/${systemPath}`, data),
  getMine: () => apiService.get(`/support/${systemPath}/mine`),
  getAll: (params = {}) => apiService.get(`/support/${systemPath}`, params),
  getOne: (id) => apiService.get(`/support/${systemPath}/${id}`),
  update: (id, data) => apiService.patch(`/support/${systemPath}/${id}`, data),
  addComment: (id, data) => apiService.post(`/support/${systemPath}/${id}/comments`, data),
  uploadAttachments: (id, files) => apiService.postFormData(`/support/${systemPath}/${id}/attachments`, attachmentsFormData(files))
});

export const supportAPI = {
  internal: {
    ...buildSupportGroup('internal'),
    // Employees eligible for assignment (HR roles + anyone with canHandleSupportTickets) - admin only
    getAssignableAgents: () => apiService.get('/support/internal/assignable-agents'),
    // HR's post-resolution rating of how the ticket was handled
    rateTicket: (id, data) => apiService.patch(`/support/internal/${id}/rating`, data)
  },
  platform: {
    ...buildSupportGroup('platform'),
    // The client admin who raised the ticket rates DigiHR's resolution
    rateTicket: (id, data) => apiService.patch(`/support/platform/${id}/rating`, data),
    // Per-DigiHR-staff performance aggregates (superadmin / platform_staff with platformSupport.read)
    getStaffPerformance: () => apiService.get('/support/platform/staff-performance')
  }
};

// ==================== DIGIHR PLATFORM STAFF API (superadmin only) ====================

export const platformStaffAPI = {
  getStaff: (params = {}) => apiService.get('/platform-staff', params),
  getStaffMember: (id) => apiService.get(`/platform-staff/${id}`),
  createStaffMember: (data) => apiService.post('/platform-staff', data),
  updateStaffMember: (id, data) => apiService.put(`/platform-staff/${id}`, data),
  toggleStaffStatus: (id) => apiService.patch(`/platform-staff/${id}/toggle-status`),
  deleteStaffMember: (id) => apiService.delete(`/platform-staff/${id}`)
};

// Policy API
export const policyAPI = {
  // Employee
  getMyPolicies: () => apiService.get('/policies/my'),
  acknowledgePolicy: (id) => apiService.post(`/policies/${id}/acknowledge`, {}),
  // Admin
  getPolicies: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return apiService.get(`/policies${q ? '?' + q : ''}`);
  },
  getPolicy: (id) => apiService.get(`/policies/${id}`),
  getPolicyStats: (id) => apiService.get(`/policies/${id}/stats`),
  createPolicy: (formData) => apiService.postFormData('/policies', formData),
  updatePolicy: (id, formData) => apiService.putFormData(`/policies/${id}`, formData),
  publishPolicy: (id) => apiService.put(`/policies/${id}/publish`, {}),
  deletePolicy: (id) => apiService.delete(`/policies/${id}`),
};

// Rewards & Recognition API
export const rewardAPI = {
  getMyRewards: () => apiService.get('/rewards/my'),
  getLeaderboard: (year) => apiService.get(`/rewards/leaderboard${year ? '?year=' + year : ''}`),
  getRewards: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return apiService.get(`/rewards${q ? '?' + q : ''}`);
  },
  createReward: (data) => apiService.post('/rewards', data),
  deleteReward: (id) => apiService.delete(`/rewards/${id}`),
};

export const peerRewardAPI = {
  givePeerReward: (data) => apiService.post('/peer-rewards', data),
  getLeaderboard: (params = {}) => { const q = new URLSearchParams(params).toString(); return apiService.get(`/peer-rewards/leaderboard${q ? '?' + q : ''}`); },
  getMyPeerRewards: (year) => apiService.get(`/peer-rewards/my${year ? '?year=' + year : ''}`),
  getGivenRewards: () => apiService.get('/peer-rewards/given'),
};

// Asset Management API
export const assetAPI = {
  getMyAssets: () => apiService.get('/assets/my'),
  getAssets: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return apiService.get(`/assets${q ? '?' + q : ''}`);
  },
  getAssetStats: () => apiService.get('/assets/stats'),
  createAsset: (data) => apiService.post('/assets', data),
  updateAsset: (id, data) => apiService.put(`/assets/${id}`, data),
  assignAsset: (id, data) => apiService.put(`/assets/${id}/assign`, data),
  returnAsset: (id, data) => apiService.put(`/assets/${id}/return`, data),
  deleteAsset: (id) => apiService.delete(`/assets/${id}`),
};

// L&D API
export const lndAPI = {
  // Employee
  getMyCourses: () => apiService.get('/lnd/my-courses'),
  getCourseForEmployee: (id) => apiService.get(`/lnd/my-courses/${id}`),
  completeLesson: (id, lessonOrder) => apiService.post(`/lnd/my-courses/${id}/complete-lesson`, { lessonOrder }),
  submitAssessment: (id, answers) => apiService.post(`/lnd/my-courses/${id}/assessment`, { answers }),
  getMyCertificates: () => apiService.get('/lnd/my-certificates'),
  // Admin
  getCourses: (params = {}) => apiService.get('/lnd/courses', params),
  getCourse: (id) => apiService.get(`/lnd/courses/${id}`),
  uploadLessonVideo: async (file) => {
    const formData = new FormData();
    formData.append('video', file);
    return apiService.postFormData('/lnd/upload-video', formData);
  },
  createCourse: (data) => apiService.post('/lnd/courses', data),
  updateCourse: (id, data) => apiService.put(`/lnd/courses/${id}`, data),
  publishCourse: (id) => apiService.put(`/lnd/courses/${id}/publish`, {}),
  deleteCourse: (id) => apiService.delete(`/lnd/courses/${id}`),
  getComplianceDashboard: () => apiService.get('/lnd/compliance'),
  getEmployeeProgress: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return apiService.get(`/lnd/progress${q ? '?' + q : ''}`);
  },
};

// Mood Check-In API
export const moodAPI = {
  getTodayStatus: () => apiService.get('/mood/today'),
  submitMood: (data) => apiService.post('/mood/submit', data),
  skipMood: () => apiService.post('/mood/skip', {}),
  getDashboard: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return apiService.get(`/mood/dashboard${q ? '?' + q : ''}`);
  },
  getTeamMoodDashboard: () => apiService.get('/mood/team'),
  getPolicy: () => apiService.get('/mood/policy'),
  updatePolicy: (data) => apiService.put('/mood/policy', data),
};

// PMS API
export const pmsAPI = {
  // Employee
  getMyGoals: (year) => apiService.get(`/pms/my-goals${year ? '?year=' + year : ''}`),
  createGoal: (data) => apiService.post('/pms/my-goals', data),
  updateGoal: (id, data) => apiService.put(`/pms/my-goals/${id}`, data),
  submitGoal: (id, action) => apiService.put(`/pms/my-goals/${id}/submit`, { action }),
  submitSelfRating: (id, data) => apiService.put(`/pms/my-goals/${id}/self-rating`, data),
  // Manager
  getTeamGoals: (year) => apiService.get(`/pms/team-goals${year ? '?year=' + year : ''}`),
  reviewGoal: (id, data) => apiService.put(`/pms/team-goals/${id}/review`, data),
  submitManagerRating: (id, data) => apiService.put(`/pms/team-goals/${id}/manager-rating`, data),
  // Admin
  getAdminDashboard: (year) => apiService.get(`/pms/dashboard${year ? '?year=' + year : ''}`),
  assignGoal: (data) => apiService.post('/pms/assign-goal', data),
  getOutcomes: (year) => apiService.get(`/pms/outcomes${year ? '?year=' + year : ''}`),
  setOutcome: (data) => apiService.post('/pms/outcomes', data),
};


// ==================== QUOTATION API (Super Admin) ====================

export const quotationAPI = {
  // Get all quotations with filters
  getQuotations: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiService.get(`/quotations${queryString ? `?${queryString}` : ''}`);
  },

  // Get single quotation
  getQuotation: (id) => apiService.get(`/quotations/${id}`),

  // Create new quotation
  createQuotation: (data) => apiService.post('/quotations', data),

  // Update quotation
  updateQuotation: (id, data) => apiService.put(`/quotations/${id}`, data),

  // Delete quotation
  deleteQuotation: (id) => apiService.delete(`/quotations/${id}`),

  // Convert an accepted quotation into an invoice
  convertToInvoice: (id, data) => apiService.post(`/quotations/${id}/convert`, data),

  // Pipeline statistics
  getStats: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiService.get(`/quotations/stats${queryString ? `?${queryString}` : ''}`);
  },
};

// ==================== INVOICE / BILLING API (Super Admin) ====================

export const invoiceAPI = {
  // Get all invoices with filters
  getInvoices: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiService.get(`/invoices${queryString ? `?${queryString}` : ''}`);
  },

  // Get single invoice (includes payment history)
  getInvoice: (id) => apiService.get(`/invoices/${id}`),

  // Create new invoice
  createInvoice: (data) => apiService.post('/invoices', data),

  // Update invoice
  updateInvoice: (id, data) => apiService.put(`/invoices/${id}`, data),

  // Delete invoice (blocked once payments exist)
  deleteInvoice: (id) => apiService.delete(`/invoices/${id}`),

  // Record a payment against an invoice
  recordPayment: (id, data) => apiService.post(`/invoices/${id}/payments`, data),

  // Remove a previously recorded payment
  deletePayment: (id, paymentId) => apiService.delete(`/invoices/${id}/payments/${paymentId}`),

  // Revenue / outstanding / overdue statistics
  getStats: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiService.get(`/invoices/stats${queryString ? `?${queryString}` : ''}`);
  },
};

// ==================== ANNOUNCEMENT API ====================

export const announcementAPI = {
  // Super Admin - full list including drafts
  getAnnouncements: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiService.get(`/announcements${queryString ? `?${queryString}` : ''}`);
  },

  // Get single announcement (includes read receipts)
  getAnnouncement: (id) => apiService.get(`/announcements/${id}`),

  // Create announcement
  createAnnouncement: (data) => apiService.post('/announcements', data),

  // Update announcement
  updateAnnouncement: (id, data) => apiService.put(`/announcements/${id}`, data),

  // Delete announcement
  deleteAnnouncement: (id) => apiService.delete(`/announcements/${id}`),

  // Any authenticated user - announcements targeted at them
  getFeed: () => apiService.get('/announcements/feed'),

  // Any authenticated user - record a read receipt
  markAsRead: (id) => apiService.post(`/announcements/${id}/read`),
};

// ==================== AUDIT LOG API (Super Admin, read-only) ====================

export const auditAPI = {
  // Get audit trail with filters
  getLogs: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiService.get(`/audit-logs${queryString ? `?${queryString}` : ''}`);
  },

  // Distinct modules + platform-side accounts, for the filter dropdowns
  getFilters: () => apiService.get('/audit-logs/filters'),

  // Activity summary
  getStats: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiService.get(`/audit-logs/stats${queryString ? `?${queryString}` : ''}`);
  },
};
